import { useEffect, useState } from 'react';
import './App.css';

import LanguageToggle from './LanguageToggle';

import {
    fahToCelsius,
    mphToMs,
    mphToKmh,
    inHgToHpa,
    inchesToMm,
    degToSimpleCardinal,
    formatBgTimeIfNeeded,
    formatValueUnit
} from "./utils";

import {
    LANG_BG,
    LANG_EN,
    DEFAULT_LANGUAGE,
    STORE_SELECTED_LANGUAGE,
//    SIGNALIR_ROUTE,
//    SIGNALIR_EVENT_NEWWEATHERDATA
} from './constants';


interface WeatherDataRecord {
    id: string;
    password: string;
    dateUtc: Date;
    dateLocalStr: string;
    baroMin: number;
    tempF: number;
    dewPtF: number;
    humidity: number;
    windSpeedMph: number;
    windGustMph: number;
    windDir: number;
    rainIn: number;
    dailyRainIn: number;
    solarRadiation: number;
    uv: number;
    indoorTempF: number;
    indoorHumidity: number;
    softwareType: string;
    action: string;
    realTime: string;
}

interface Language {
    loading: string;
    currentWeather: string;
    time: string;
    temperature: string;
    dewPoint: string;
    humidity: string;
    barometricPressure: string;
    windSpeed: string;
    windGust: string;
    windDirection: string;
    rainfall: string;
    dailyRainfall: string;
    solarRadiation: string;
    uvIndex: string;
    indoorTemperature: string;
    indoorHumidity: string;
    metersPerSec: string;
    kmPerHour: string;
    millimiters: string;
}

const languages: {
    english: Language;
    bulgarian: Language;
    [key: string]: Language; // Index signature
} = {
    english: {
        loading: 'Loading...',
        currentWeather: 'Current Weather',
        time: 'Time',
        temperature: 'Temperature',
        dewPoint: 'Dew Point',
        humidity: 'Humidity',
        barometricPressure: 'Barometric Pressure',
        windSpeed: 'Wind Speed',
        windGust: 'Wind Gust',
        windDirection: 'Wind Direction',
        rainfall: 'Rainfall',
        dailyRainfall: 'Daily Rainfall',
        solarRadiation: 'Solar Radiation',
        uvIndex: 'UV Index',
        indoorTemperature: 'Indoor Temperature',
        indoorHumidity: 'Indoor Humidity',
        metersPerSec: 'm/s',
        kmPerHour: 'km/h',
        millimiters: 'mm',
    },
    bulgarian: {
        loading: 'Зареждане...',
        currentWeather: 'Времето сега',
        time: 'Час',
        temperature: 'Температура',
        dewPoint: 'Точка на оросяване',
        humidity: 'Влажност',
        barometricPressure: 'Барометрично налягане',
        windSpeed: 'Скорост на вятъра',
        windGust: 'Порив на вятъра',
        windDirection: 'Посока на вятъра',
        rainfall: 'Валежи',
        dailyRainfall: 'Дневни валежи',
        solarRadiation: 'Слънчево излъчване',
        uvIndex: 'UV индекс',
        indoorTemperature: 'Стайна температура',
        indoorHumidity: 'Стайна влажност',
        metersPerSec: 'м/с',
        kmPerHour: 'км/ч',
        millimiters: 'мм',
    },
};

function App() {
    const [weatherData, setWeather] = useState<WeatherDataRecord>();
    const [language, setLanguage] = useState<string>(DEFAULT_LANGUAGE);
    //const [eventSource, setEventSource] = useState<EventSource | null>(null);

    const handleLanguageChange = () => {
        const newLanguage = language === LANG_EN ? LANG_BG : LANG_EN;
        console.log(`Changing language to: ${newLanguage}`);

        setLanguage(newLanguage);
        localStorage.setItem(STORE_SELECTED_LANGUAGE, newLanguage);
    };

    useEffect(() => {
        const storedLanguage = localStorage.getItem(STORE_SELECTED_LANGUAGE);
        if (storedLanguage) {
            console.log(`Restoring language: ${storedLanguage}`);
            setLanguage(storedLanguage);
        }
    }, []);

    //useEffect(() => {
    //    // Create a new EventSource for receiving SSE events
    //    const newEventSource = new EventSource(SIGNALIR_ROUTE);

    //    // Handle incoming events
    //    newEventSource.onmessage = (event) => {
    //        const newData = JSON.parse(event.data);
    //        console.log(newData);
    //        setWeather(newData);
    //    };

    //    // Cleanup when the component unmounts
    //    return () => {
    //        newEventSource.close();
    //    };
    //}, []); // Run the effect only once when the component mounts

    /*useEffect(() => {
        // Create a new SignalR connection
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(SIGNALIR_ROUTE) // Replace with your server URL
            .withAutomaticReconnect()
            .build();

        connection.start().catch(err => console.error(err));

        // Handle incoming events
        connection.on(SIGNALIR_EVENT_NEWWEATHERDATA, (data: WeatherDataRecord) => {
            console.log('SignalR:', data);
            setWeather(data);
        });

        // Cleanup when the component unmounts
        return () => {
            connection.stop();
        };
    }, []);*/

    useEffect(() => {
        // Create a new EventSource for receiving SSE events
        const eventSource = new EventSource('/weather/sse');

        // Handle incoming events
        eventSource.onmessage = (event) => {
            const data = JSON.parse(event.data);
            console.log('SSE:', data);
            setWeather(data);
        };

        // Cleanup when the component unmounts
        return () => {
            eventSource.close();
        };
    }, []);

    useEffect(() => {
        // Call the populateWeatherData function once when the component mounts
        populateWeatherData();

        // Set an interval to call the populateWeatherData function every 1 second(s)
        const intervalID = setInterval(() => {
            populateWeatherData();
        }, 60 * 1000); 

        // Clear the interval when the component unmounts or the weatherData changes
        return () => {
            clearInterval(intervalID);
        };
    }, []); // Run the effect whenever the weatherData changes



    const contents = weatherData === undefined
        ? <p><em>{languages[language].loading}</em></p>
        : (
            <table className="weather-table" aria-labelledby="tabelLabel">
                <thead>
                    <tr>
                        <th colSpan={2} style={{ textAlign: 'center' }}>
                            {languages[language].currentWeather}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{languages[language].time}</td>
                        <td>{formatValueUnit(formatBgTimeIfNeeded(weatherData.dateUtc, language), '')}</td>
                    </tr>
                    <tr>
                        <td>{languages[language].temperature}</td>
                        <td>{formatValueUnit(fahToCelsius(weatherData.tempF), ' \u00B0C', 1)}</td>
                    </tr>
                    <tr>
                        <td>{languages[language].dewPoint}</td>
                        <td>{formatValueUnit(fahToCelsius(weatherData.dewPtF), ' \u00B0C', 1)}</td>
                    </tr>
                    <tr>
                        <td>{languages[language].humidity}</td>
                        <td>{formatValueUnit(weatherData.humidity, ' %', 0)}</td>
                    </tr>
                    <tr>
                        <td>{languages[language].barometricPressure}</td>
                        <td>{formatValueUnit(inHgToHpa(weatherData.baroMin), ' hPa', 0)}</td>
                    </tr>
                    <tr>
                        <td>{languages[language].windSpeed}</td>
                        <td>
                            <span className={"nowrap"} style={{ marginRight: "0.5em" }}>
                                {formatValueUnit(mphToMs(weatherData.windSpeedMph), " " + languages[language].metersPerSec, 1)}
                            </span>
                            <span> </span>
                            <span className={"nowrap"} style={{ marginRight: "0.5em" }}>
                                {formatValueUnit(mphToKmh(weatherData.windSpeedMph), " " + languages[language].kmPerHour, 1)}
                            </span>
                        </td>
                    </tr>
                    <tr>
                        <td>{languages[language].windGust}</td>
                        <td>
                            <span className={"nowrap"} style={{ marginRight: "0.5em" }}>
                                {formatValueUnit(mphToMs(weatherData.windGustMph), " " + languages[language].metersPerSec, 1)}
                            </span>
                            <span> </span>
                            <span className={"nowrap"} style={{ marginRight: "0.5em" }}>
                                {formatValueUnit(mphToKmh(weatherData.windGustMph), " " + languages[language].kmPerHour, 1)}
                            </span>
                        </td>
                    </tr>
                    <tr>
                        <td>{languages[language].windDirection}</td>
                        <td>{formatValueUnit(degToSimpleCardinal(weatherData.windDir, language), " " + weatherData.windDir?.toString() + "\u00B0")}</td>
                    </tr>
                    <tr>
                        <td>{languages[language].rainfall}</td>
                        <td>{formatValueUnit(inchesToMm(weatherData.rainIn), " " + languages[language].millimiters, 0)}</td>
                    </tr>
                    <tr>
                        <td>{languages[language].dailyRainfall}</td>
                        <td>{formatValueUnit(inchesToMm(weatherData.dailyRainIn), " " + languages[language].millimiters, 0)}</td>
                    </tr>
                    <tr>
                        <td>{languages[language].solarRadiation}</td>
                        <td>{formatValueUnit(weatherData.solarRadiation, " W/m\u00B2", 1)}</td>
                    </tr>
                    <tr>
                        <td>{languages[language].uvIndex}</td>
                        <td>{formatValueUnit(weatherData.uv, "", 1)}</td>
                    </tr>
                    <tr>
                        <td>{languages[language].indoorTemperature}</td>
                        <td>{formatValueUnit(fahToCelsius(weatherData.indoorTempF), ' \u00B0C', 1)}</td>
                    </tr>
                    <tr>
                        <td>{languages[language].indoorHumidity}</td>
                        <td>{formatValueUnit(weatherData.indoorHumidity, ' %', 0)}</td>
                    </tr>
                </tbody>
            </table>
            )

    return (
        <div>
            {/*<h2 id="tabelLabel">Current Weather</h2>*/}
            {contents}
            <LanguageToggle onLanguageChange={handleLanguageChange} />
        </div>
    );

    async function populateWeatherData() {
        const response = await fetch('weather');
        const data = await response.json();

        console.log(`Periodic:`, data);
        setWeather(data);
    }
}

export default App;